import Communication from './Communication';
import Popup from './Popup';
import Dropdown from './Dropdown';
import SideBySide from './SideBySide';
import Helper from './Helper';

import '../assets/scss/floating.scss';
import '../assets/scss/hacks.scss';

class LinguiseSwitcher {
    /**
     * Initialize the switcher
     * @param {HTMLScriptElement | undefined} currentScript 
     */
    constructor(currentScript) {
        /**
         * Current domain
         */
        this.domain = null;

        /**
         * The actual base domain URL (that is registered)
         */
        this.base_url = null;

        /**
         * Domain id
         */
        this.domain_id = null;

        /**
         * The language of the page loaded
         *
         * @type {null|string}
         */
        this.current_language = null;

        /**
         * Base language code of the website
         *
         * @type {null|string}
         */
        this.default_language = null;

        /**
         * Translation of urls
         * @type {null|boolean}
         */
        this.translate_urls = null;

        /**
         * Available languages for this domain
         *
         * @type {*[]}
         */
        this.available_languages = [];

        /**
         * Scheme of the current page
         *
         * @type {null|string}
         */
        this.scheme = null;

        /**
         * Host of the current page
         *
         * @type {null|string}
         */
        this.host = null;

        /**
         * Original path of the current page
         *
         * @type {string}
         */
        this.original_path = '';

        /**
         * Is alternate link already injected via CMS
         */
        this.alternate_link_injected = false;

        /**
         * Is flag is automatic injected by CMS
         * 
         * Default to true
         */
        this.cms_automatic_switcher = false;

        /**
         * Base path of the current page
         *
         * @type {null|string}
         */
        this.path = null;

        /**
         * Trailing slashes of the current page
         * 
         * @type {string}
         */
        this.trailing_slashes = '';

        /**
         * Search of the current page
         *
         * @type {null|string}
         */
        this.search = null;

        /**
         * All url tanslations of this page
         *
         * @type {{}}
         */
        this.url_translations = {};

        /**
         * Language settings
         *
         * @type {{}}
         */
        this.language_settings = {};

        /**
         * Domain structure
         *
         * @type {null|string}
         */
        this.domain_structure = null;

        /**
         * Available only for WP or Joomla
         *
         * @type {{}}
         */
        this.linguise_configs = {};

        /**
         * Demo mode
         */
        this.demo_mode = false;

        this._current_script = currentScript;
    }

    /**
     * Initialize whole switcher
     * @return {Promise<void>}
     */
    async initialize()
    {
        // Set configs
        this.setConfigs();

        if (Object.keys(this.linguise_configs).length === 0 && !await this.getDomainConfig()) {
            // Not able to retrieve domain configuration
            return;
        }

        const language = this.getCurrentLanguage();

        if (this.getPath() !== '/' && this.translate_urls) {
            // Retrieve url translation from API
            this.url_translations = await Communication.getUrlTranslations(language, this.getPath());
        }

        // Inject alternates
        this.addAlternates();

        this.addFlag();


        if (this.platform === 'shopify') {
            if (!this.pathShopifyCookies()) {
                const i = setInterval(() => {
                    if (this.pathShopifyCookies()) {
                        clearInterval(i);
                    }
                }, 1000);
            }
        }
    }

    /**
     * Extract cookies and look for cart cookie
     * As cookies won't be share from top level domain to subdomain,
     * we need to add the cart cookie to the subdomains
     * The cookie will be added to .domain.ltd
     *
     * @return {boolean}
     */
    pathShopifyCookies() {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.startsWith('cart=')) {
                // Add the same cookie for the .domain.ltd
                const new_cookie = 'cart=' + cookie.substring(5) + ';domain=.' + this.domain.split('//', 2)[1] + ';path=/';
                document.cookie = new_cookie;
                return true;
            }
        }
        return false;
    }

    /**
     * Retrieve the current page language
     *
     * @return {string|null}
     */
    getCurrentLanguage()
    {
        if (this.current_language !== null) {
            return this.current_language;
        }

        let language = this.default_language;
        for (const index in this.available_languages) {
            if (this.available_languages[index].code === document.documentElement.lang) {
                language = document.documentElement.lang;
                break;
            }
        }

        this.current_language = language;

        return this.current_language;
    }

    /**
     * Retrieve domain configuration from Linguise API
     *
     * @return {Promise<boolean>}
     */
    async getDomainConfig()
    {
        const config = await Communication.getConfig(this.domain_id);

        if (!config) {
            return false;
        }

        this.base_url = config.url;
        this.domain = config.domain;
        this.available_languages = config.languages;
        this.default_language = config.language;
        this.translate_urls = !!config.translate_urls;
        this.language_settings = config.language_settings;
        this.domain_structure = config.structure;

        return true;
    }

    /**
     * Add flag to the DOM html
     */
    addFlag(language_settings = {})
    {
        if (Object.keys(language_settings).length > 0) {
            this.language_settings = language_settings;
        }

        switch (this.language_settings?.display) {
            case 'dropdown': {
                const dropdown = new Dropdown();
                this._switcher = dropdown;
                this._switcher_element = dropdown.load(this);
                dropdown.postLoad(this);
                break;
            }
            case 'popup': {
                const popup = new Popup();
                this._switcher = popup;
                this._switcher_element = popup.load(this);
                popup.postLoad(this);
                break;
            }
            case 'side_by_side': {
                const sideBySide = new SideBySide();
                this._switcher = sideBySide;
                this._switcher_element = sideBySide.load(this);
                break;
            }
            default: {
                const popup = new Popup();
                this._switcher = popup;
                this._switcher_element = popup.load(this);
                break;
            }
        }

        this._switcher.postLoad(this);
        this.monitorDOM();

        import('../assets/scss/floating.scss');
    }

    /**
     * Inject alternates to the head
     */
    addAlternates()
    {
        if (this.alternate_link_injected) {
            // We don't need to do this again.
            return;
        }

        this.available_languages.forEach((available_languages) => {
            this.injectAlternate(available_languages.code);
        });
        this.injectAlternate('x-default');
    }

    /**
     * Generate and inject an alternate
     *
     * @param language
     */
    injectAlternate(language)
    {
        const element = document.createElement('link');

        element.rel = 'alternate';
        element.hreflang = language;
        if (language === 'x-default' || language === this.default_language) {
            element.href = Helper.fixTrailingSlashes(this, this.base_url);
        } else {
            element.href = Helper.getLanguageUrl(this, language);
        }

        document.head.appendChild(element);
    }

    /**
     * Retrieve current page path
     *
     * @return {string}
     */
    getPath()
    {
        if (this.path !== null) {
            return this.path;
        }

        if (this.demo_mode) {
            this.path = '/';
            this.trailing_slashes = '';
            this.search = '';

            return this.path;
        }

        // Domain base url
        const base_url = new URL(this.domain);

        this.path = document.location.pathname.replace(/\/+$/, '');
        this.trailing_slashes = document.location.pathname.endsWith('/') ? '/' : '';
        this.search = document.location.search;
        if (base_url.pathname !== '/') {
            this.path = this.path.substring(base_url.pathname.length);
        }
        this.scheme = base_url.protocol;
        this.host = base_url.host;

        for (let ij = 0; ij < this.available_languages.length; ij++) {
            const regex = new RegExp('^/(?:' + this.available_languages[ij].code + ')($|/.*)');
            const result = regex.exec(this.path);
            if (result) {
                this.path = result[1];
                break;
            }
        }

        if (this.path === '') {
            this.path = '/';
        }

        return this.path;
    }

    setConfigs() {
        this.is_joomla = !!(typeof linguise_configs !== 'undefined' && linguise_configs.vars.configs.joomla_languages);
        this.is_wp = !!(typeof linguise_configs !== 'undefined' && !linguise_configs.vars.configs.joomla_languages);

        if (this.is_joomla) {
            return this.setJoomlaLinguiseConfig();
        }

        if (this.is_wp) {
            return this.setWPLinguiseConfig();
        }

        // Retrieve domain base64 from url params
        const current_script = this._current_script || document.currentScript;
        if (current_script) {
            const url = new URL(current_script.src);

            const public_key = url.searchParams.get('d');
            const domain_id = url.searchParams.get('i');
    
            if (!public_key && !Communication._stored_pkey) {
                return;
            }
    
            Communication.setPublicKey(public_key);
    
            this.domain_id = domain_id || null;
        }
    }

    getCurrentLocation() {
        const url = new URL(window.location.href);

        const trailing_slashes = url.pathname.endsWith('/') ? '/' : '';

        return `${url.protocol}//${url.host}${trailing_slashes}`;
    }

    setJoomlaLinguiseConfig() {
        const {
            flag_display_type, language_default, display_position, languages, base_url,
            enable_language_name, enable_flag, enable_language_short_name, language_name_display,
            alternate_link, original_path, automatic_switcher,
        } = linguise_configs.vars.configs;
        this.domain = base_url || this.getCurrentLocation();
        this.base_url = base_url || this.getCurrentLocation();
        this.linguise_configs = linguise_configs.vars.configs;
        this.available_languages = Object.keys(languages).map((lang) => {
            return {
                code: lang,
                name: languages[lang],
                original_name: languages[lang],
            }
        });
        this.default_language = language_default;
        this.alternate_link_injected = Boolean(alternate_link);
        this.cms_automatic_switcher = Boolean(automatic_switcher);
        this.original_path = original_path;
        this.language_settings = {
            display: flag_display_type,
            position: display_position,
            enabled_lang_name: parseInt(enable_language_name),
            enabled_lang_short_name: parseInt(enable_language_short_name),
            enabled_flag: parseInt(enable_flag),
            lang_name_display: language_name_display,
            ...this.linguise_configs,
        };
    }

    setWPLinguiseConfig() {
        const {
            flag_display_type, default_language, display_position, languages, base_url, demo_mode,
            enable_language_short_name, enable_language_name, enable_flag, language_name_display,
            alternate_link, original_path, add_flag_automatically,
        } = linguise_configs.vars.configs;
        const current_dir = this.getCurrentLocation();
        if (demo_mode) {
            this.base_url = current_dir;
            this.domain = current_dir;
        } else {
            this.base_url = base_url || current_dir;
            this.domain = base_url || current_dir;
        }
        this.demo_mode = Boolean(demo_mode);
        this.linguise_configs = linguise_configs.vars.configs;
        this.available_languages = Object.keys(languages).map((lang) => {
            return {
                code: lang,
                name: languages[lang],
                original_name: languages[lang],
            }
        });
        this.default_language = default_language;
        this.original_path = original_path;
        this.alternate_link_injected = Boolean(alternate_link);
        this.cms_automatic_switcher = Boolean(add_flag_automatically);
        this.language_settings = {
            display: flag_display_type,
            position: display_position,
            enabled_lang_short_name: enable_language_short_name,
            enabled_lang_name: enable_language_name,
            enabled_flag: enable_flag,
            lang_name_display: language_name_display,
            ...this.linguise_configs,
        };
    }

    /**
     * Filter unique element nodes
     * @param {HTMLElement} nodes the nodes to filter
     * @returns {HTMLElement[]}
     */
    _uniqueElementNode(nodes) {
        const seen = new Set();
        const unique = [];
        for (const node of nodes) {
            if (seen.has(node)) {
                continue;
            }

            seen.add(node);
            unique.push(node);
        }
        return unique;
    }

    /**
     * Find linguise switcher root element
     * @param {HTMLElement} baseElement the base element to check/verify
     * @returns {HTMLElement[]}
     */
    _findLinguiseSwitcherRoot(baseElement) {
        const collectedElements = [];

        if (baseElement.nodeType === Node.ELEMENT_NODE) {
            let hitElements = false;
            if (baseElement.classList && baseElement.classList.contains('linguise_switcher_root')) {
                collectedElements.push(baseElement);
                hitElements = true;
            } else if (baseElement.textContent?.trim() === '[linguise]') {
                baseElement.__linguise_in_place = true;
                collectedElements.push(baseElement);
                hitElements = true;
            }

            if (!hitElements) {
                for (let i = 0; i < baseElement.children?.length; i++) {
                    collectedElements.push(...this._findLinguiseSwitcherRoot(baseElement.children[i]));
                }
            }
        }

        return collectedElements;
    }

    /**
     * Monitor DOM for changes so we can always update the linguise switcher
     */
    monitorDOM() {
        const observer = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                let hasNewElement = false;
                if (mutation.type === 'childList') {
                    // Get added element and check if it has linguise_switcher_root class
                    mutation.addedNodes.forEach((element) => {
                        if (!element.isConnected) {
                            // Node already removed
                            return;
                        }

                        // Deep search the addedNodes
                        const switcherElements = this._uniqueElementNode(this._findLinguiseSwitcherRoot(element));
                        if (switcherElements.length > 0 && this._switcher && this._switcher_element) {
                            const language_settings = this.language_settings;

                            // We found many switcher node, replace them.
                            switcherElements.forEach((switcherEl) => {
                                const is_menu = switcherEl.classList?.contains('linguise_menu_root') || element.classList?.contains('linguise_menu') || switcherEl?.__linguise_in_place;

                                Helper.replaceAndPaintElement(switcherEl, this._switcher_element, language_settings);
                                if (!is_menu && !this.demo_mode) {
                                    // If not menu, position properly
                                    Helper.languagePosition(switcherEl, language_settings?.display, language_settings?.position);
                                }
                                hasNewElement = true;
                            });
                        }
                    });

                    const target = mutation.target;
                    if (target.nodeType === Node.ELEMENT_NODE && this._switcher_element && this._switcher) {
                        const language_settings = this.language_settings;
                        if (target.classList && target.classList.contains('linguise_switcher_root')) {
                            const is_menu = target.classList.contains('linguise_menu_root') || target.classList.contains('linguise_menu');
                            Helper.replaceAndPaintElement(target, this._switcher_element, language_settings);
                            if (!is_menu && !this.demo_mode) {
                                Helper.languagePosition(target, language_settings?.display, language_settings?.position);
                            }
                            hasNewElement = true;
                        } else if (target.textContent?.trim() === '[linguise]') {
                            Helper.replaceAndPaintElement(target, this._switcher_element,language_settings);
                            hasNewElement = true;
                        }
                    }
                }

                if (hasNewElement && this._switcher) {
                    this._switcher.postLoad(this);
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}

export default LinguiseSwitcher;
